import React, { useMemo } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import moment from "moment";
import { Skeleton } from "../ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { BarChart3Icon } from "lucide-react";
import { cn } from "../../lib/utils";
import AnalyticsDateFilter, { QuickPeriod } from "./AnalyticsDateFilter";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface RequestAnalyticsData {
  timestamp: number;
  peak_requests_per_minute?: number;
  avg_requests_per_minute?: number;
  requests_per_minute?: number; // For backward compatibility
}

interface RequestAnalyticsChartProps {
  data: RequestAnalyticsData[];
  loading: boolean;
  title?: string;
  className?: string;
  onQuickPeriodSelect?: (period: QuickPeriod) => void;
  selectedPeriod?: QuickPeriod | null;
  periodDays?: number;
}

const RequestAnalyticsChart: React.FC<RequestAnalyticsChartProps> = ({
  data,
  loading,
  title = "Request Activity",
  className,
  onQuickPeriodSelect,
  selectedPeriod,
  periodDays,
}) => {
  const chartData = useMemo(() => {
    
    if (!data || data.length === 0) {
      return {
        labels: [],
        datasets: [],
      };
    }

    // Log first data point to understand structure

    // Handle both old and new data formats
    const validData = data.filter(point => {
      if (!point.timestamp) return false;
      
      // New format with peak and avg
      if (typeof point.peak_requests_per_minute === "number" && 
          typeof point.avg_requests_per_minute === "number") {
        return !isNaN(point.peak_requests_per_minute) && !isNaN(point.avg_requests_per_minute);
      }
      
      // Old format with just requests_per_minute (fallback)
      if (typeof point.requests_per_minute === "number") {
        // Convert old format to new format
        point.peak_requests_per_minute = point.requests_per_minute;
        point.avg_requests_per_minute = point.requests_per_minute;
        return !isNaN(point.requests_per_minute);
      }
      
      return false;
    });

    // Dynamic label formatting based on period
    // Convert UTC timestamps to local timezone for display
    const formatLabel = (timestamp: number) => {
      const localTime = moment(timestamp);
      
      if (periodDays && periodDays <= 1) {
        // For 24 hours or less, determine optimal format based on data span
        if (validData.length <= 1) {
          return localTime.format("h:mm A");
        }
        
        // Calculate time span of the data
        const timestamps = validData.map(d => d.timestamp).sort((a, b) => a - b);
        const timeSpanHours = (timestamps[timestamps.length - 1] - timestamps[0]) / (1000 * 60 * 60);
        
        if (timeSpanHours <= 6) {
          // For 6 hours or less, show hour:minute
          return localTime.format("h:mm A");
        } else if (timeSpanHours <= 12) {
          // For 6-12 hours, show hour:minute but less frequently
          const minutes = localTime.minutes();
          if (minutes === 0 || minutes === 30) {
            return localTime.format("h:mm A");
          } else {
            return localTime.format("h A");
          }
        } else {
          // For 12-24 hours, show hour only but ensure we have enough granularity
          return localTime.format("h A");
        }
      } else {
        // For more than 24 hours, show only days in local timezone
        return localTime.format("MMM D");
      }
    };

    // Use requests per minute directly (matching backend calculation)
    const peakData = validData.map(point => point.peak_requests_per_minute);
    const avgData = validData.map(point => point.avg_requests_per_minute);
    

    return {
      labels: validData.map(point => formatLabel(point.timestamp)),
      datasets: [
        {
          label: "Peak RPM",
          data: peakData,
          borderColor: "#ef4444", // Red for peak
          backgroundColor: "rgba(239, 68, 68, 0.1)",
          borderWidth: 2,
          fill: false,
          tension: 0.3,
          pointRadius: 0,
          pointHoverRadius: 4,
          pointHoverBackgroundColor: "#ef4444",
          pointHoverBorderColor: "#ffffff",
          pointHoverBorderWidth: 2,
        },
        {
          label: "Average RPM",
          data: avgData,
          borderColor: "#21b892", // Maitai green for average
          backgroundColor: "rgba(33, 184, 146, 0.1)",
          borderWidth: 2,
          fill: true,
          tension: 0.3,
          pointRadius: 0,
          pointHoverRadius: 4,
          pointHoverBackgroundColor: "#21b892",
          pointHoverBorderColor: "#ffffff",
          pointHoverBorderWidth: 2,
        },
      ],
    };
  }, [data, periodDays]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: "index" as const,
    },
    plugins: {
      legend: {
        display: true,
        position: "top" as const,
        labels: {
          color: "#737373",
          font: {
            size: 12,
          },
          usePointStyle: true,
          pointStyle: "line",
        },
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        titleColor: "#ffffff",
        bodyColor: "#ffffff",
        borderColor: "#21b892",
        borderWidth: 1,
        cornerRadius: 6,
        displayColors: false,
        callbacks: {
          title: function(context: any) {
            // Always show full date and time in tooltip in user's local timezone
            const dataIndex = context[0].dataIndex;
            if (data && data[dataIndex]) {
              // Unix timestamps from backend are in milliseconds since epoch
              return moment(data[dataIndex].timestamp).format("MMM D, YYYY [at] h:mm A");
            }
            return context[0].label;
          },
          label: function(context: any) {
            const value = context.parsed.y;
            const datasetLabel = context.dataset.label;
            // Show requests per minute directly
            const rpm = value.toFixed(2);
            const rpmType = datasetLabel.includes("Peak") ? "Peak" : "Average";
            return `${rpm} RPM ${rpmType}`;
          },
        },
      },
    },
    scales: {
      x: {
        type: "category" as const,
        grid: {
          display: false,
        },
        ticks: {
          color: "#737373", // muted-foreground
          font: {
            size: 12,
          },
          maxTicksLimit: periodDays && periodDays <= 1 ? 12 : 8,
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: "rgba(115, 115, 115, 0.1)", // muted with transparency
        },
        ticks: {
          color: "#737373", // muted-foreground
          font: {
            size: 12,
          },
          callback: function(value: any) {
            // Format as whole numbers only
            return Math.round(value);
          },
          // Ensure we only show whole number ticks
          stepSize: 1,
        },
      },
    },
  }), [data]);

  if (loading) {
    return (
      <Card className={cn("border-border", className)}>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
            <BarChart3Icon className="w-5 h-5 text-maitai-green" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="h-[250px] flex items-center justify-center">
            <div className="space-y-3 w-full">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-32 w-full" />
              <div className="grid grid-cols-4 gap-2">
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-full" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card className={cn("border-border", className)}>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
              <BarChart3Icon className="w-5 h-5 text-maitai-green" />
              {title}
              {periodDays && (
                <span className="text-sm font-gc-reg text-muted-foreground">
                  {periodDays === 1 ? "Last 24 hours" :
                   periodDays === 7 ? "Last week" : 
                   periodDays === 30 ? "Last month" :
                   `Last ${periodDays} days`}
                </span>
              )}
            </CardTitle>
            {onQuickPeriodSelect && (
              <AnalyticsDateFilter
                onQuickPeriodSelect={onQuickPeriodSelect}
                selectedPeriod={selectedPeriod}
              />
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-[250px] flex items-center justify-center">
            <div className="text-center space-y-3">
              <h3 className="text-lg font-gc-med text-foreground">
                No request data available
              </h3>
              <p className="text-muted-foreground max-w-md">
                Request analytics will appear here once your application starts receiving traffic.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn("border-border", className)}>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
            <BarChart3Icon className="w-5 h-5 text-maitai-green" />
            {title}
            {periodDays && (
              <span className="text-sm font-gc-reg text-muted-foreground">
                {periodDays === 1 ? "Last 24 hours" :
                 periodDays === 7 ? "Last week" : 
                 periodDays === 30 ? "Last month" :
                 `Last ${periodDays} days`}
              </span>
            )}
          </CardTitle>
          {onQuickPeriodSelect && (
            <AnalyticsDateFilter
              onQuickPeriodSelect={onQuickPeriodSelect}
              selectedPeriod={selectedPeriod}
            />
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-[250px]">
          {loading ? (
            <div className="space-y-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-4/5" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-4 w-4/5" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-4 w-full" />
            </div>
          ) : (
            <Line data={chartData} options={options} />
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default RequestAnalyticsChart;